// Single source of truth for app destinations. Navigation (desktop bar, user
// menu, help menu, mobile sheet) and GlobalSearch quick links all read from
// here so the menus and the search palette never drift apart.
//
// Labels are untranslated i18n keys — callers pass them through $t / t.
// `keywords` are extra (English + Romanian) terms matched by search.

export const NAV_SECTIONS = {
  workspace: 'Workspace',
  account: 'Account',
  admin: 'Administration',
  help: 'Help & resources',
};

const ROLE_ICONS = {
  Correctors: 'fa-check-double',
  Admins: 'fa-user-shield',
  SuperAdmins: 'fa-user-gear',
};

export const roleRouteIcon = (route) => ROLE_ICONS[route?.meta?.requires] || 'fa-folder';

/**
 * @param {object} opts
 * @param {boolean} opts.isLoggedIn
 * @param {boolean} opts.isProductionRadio
 * @param {Array}   opts.roleRoutes  top-level router records the user's groups unlock (non-Users)
 * @param {string}  opts.supportEmail
 */
export const buildNavCatalog = ({ isLoggedIn, isProductionRadio, roleRoutes = [], supportEmail } = {}) => {
  const items = [];

  if (isLoggedIn) {
    items.push(
      { id: 'dashboard', section: 'workspace', label: 'Dashboard', icon: 'fa-gauge-high', route: 'UserDashboard', path: '/Users/dashboard', keywords: 'home overview panou' },
      { id: 'transcribe', section: 'workspace', label: 'Transcribe', icon: 'fa-microphone', route: 'TranscriptEditorDashboard', path: '/Users/transcriptordashboard', keywords: 'editor upload new transcription transcriere incarca' },
      { id: 'transcripts', section: 'workspace', label: 'Transcripts', icon: 'fa-file-lines', route: 'SavedTranscripts', path: '/Users/transcripts', keywords: 'library files transcrieri salvate transcripts transcrieri' },
      { id: 'assets', section: 'workspace', label: 'Assets', icon: 'fa-folder-open', route: 'AssetsBrowser', path: '/Users/assets', keywords: 'files media storage fisiere' },
    );
    if (!isProductionRadio) {
      items.push({ id: 'video', section: 'workspace', label: 'Video', icon: 'fa-video', route: 'VideoGeneratorDashboard', path: '/Users/videodashboard', keywords: 'generator shorts clips' });
    }

    items.push(
      { id: 'profile', section: 'account', label: 'Edit Profile', icon: 'fa-user-pen', route: 'UserProfile', path: '/Users/profile', keywords: 'profile settings account profil setari' },
      { id: 'orders', section: 'account', label: 'Orders', icon: 'fa-receipt', route: 'UsersOrders', path: '/Users/orders', keywords: 'billing invoices history comenzi facturi' },
      { id: 'team', section: 'account', label: 'Team', icon: 'fa-users', route: 'TeamManager', path: '/Users/team', keywords: 'members collaborators invite echipa' },
    );
    if (!isProductionRadio) {
      items.push({ id: 'subscription', section: 'account', label: 'Subscription & credits', icon: 'fa-coins', action: 'subscription', keywords: 'plan upgrade credits billing abonament credite' });
    }

    roleRoutes.forEach((r) => {
      items.push({
        id: `role-${r.path}`,
        section: 'admin',
        label: r.displayname || r.name || r.path,
        icon: roleRouteIcon(r),
        route: r.name || r.redirect || r.path,
        path: r.path,
        keywords: 'admin manage',
      });
    });
  }

  if (!isProductionRadio) {
    items.push(
      { id: 'guides', section: 'help', label: 'User guides', icon: 'fa-book-open', route: 'Docs', path: '/docs', keywords: 'support faq guide guides docs documentation ajutor ghiduri documentatie' },
      { id: 'help', section: 'help', label: 'Help', icon: 'fa-life-ring', route: 'Help', path: '/help', keywords: 'support faq guide ajutor' },
      { id: 'api-docs', section: 'help', label: 'API Docs', icon: 'fa-code', route: 'ApiDocs', path: '/api-docs', keywords: 'developer integration api' },
      { id: 'about', section: 'help', label: 'About', icon: 'fa-circle-info', route: 'About', path: '/about', keywords: 'despre' },
      { id: 'terms', section: 'help', label: 'Terms', icon: 'fa-file-contract', route: 'TermsAndConditions', path: '/terms', keywords: 'legal conditions termeni' },
      { id: 'privacy', section: 'help', label: 'Privacy Policy', icon: 'fa-shield-halved', route: 'PrivacyPolicy', path: '/privacy', keywords: 'legal gdpr data confidentialitate' },
      { id: 'data-handling', section: 'help', label: 'Data Handling', icon: 'fa-database', route: 'DataHandling', path: '/data-handling', keywords: 'data storage retention delete gdpr aws stocare pastrare stergere prelucrarea datelor' },
    );
    if (supportEmail) {
      items.push({ id: 'support', section: 'help', label: 'Contact support', icon: 'fa-envelope', href: `mailto:${supportEmail}`, keywords: 'email contact suport' });
    }
  }

  return items;
};

export const itemsInSection = (catalog, section) => catalog.filter((item) => item.section === section);

/** Case/diacritic-insensitive match against the translated label + keywords. */
const fold = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const matchCatalog = (catalog, query, translate = (s) => s) => {
  const q = fold(query).trim();
  if (!q) return [];
  return catalog
    .map((item) => {
      const label = fold(translate(item.label));
      const hay = `${label} ${fold(item.label)} ${fold(item.keywords)}`;
      if (!hay.includes(q)) return null;
      // Rank label-prefix matches above keyword-only matches.
      const score = label.startsWith(q) ? 0 : label.includes(q) ? 1 : 2;
      return { item, score };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .map(({ item }) => item);
};
